import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch, FaEdit } from 'react-icons/fa';
import { 
  TextField, 
  Button, 
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainContainer } from '../../common/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import CountrySelector from '../../common/CountrySelector';
import ClientModal from '../../common/ClientModal';
import { useAlert } from '../../../contexts/AlertContext';
import { organizationApi } from '../../../services/api';
import { COUNTRY_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import { getClientPartyTypeName, getClientPartyTypeBackgroundColor, getClientPartyTypeTextColor } from '../../../constants/clientPartyTypes';

const ClientsConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [clients, setClients] = useState([]);
  const [countries, setCountries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  
  // Unified modal state
  const [clientModal, setClientModal] = useState({ 
    open: false, 
    isEdit: false, 
    initialData: null
  });
  
  const [deleteModal, setDeleteModal] = useState({ open: false, client: null });
  const [isLoading, setIsLoading] = useState(true);

  const fetchClients = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getClients();
      setClients(data);
    } catch (err) {
      showAlert('Failed to load clients.', 'error', err);
      console.error('Error fetching clients:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const data = await organizationApi.getCountries();
      setCountries(data);
    } catch (err) {
      showAlert('Failed to load countries.', 'error', err);
      console.error('Error fetching countries:', err);
    }
  };

  useEffect(() => {
    Promise.all([fetchClients(), fetchCountries()]);
  }, []);

  const handleClientCreated = async () => {
    // Refresh the clients list when a client is created/updated
    await fetchClients();
  };

  const handleCloseClientModal = () => {
    setClientModal({
      open: false,
      isEdit: false,
      initialData: null
    });
  };

  const handleDeleteClient = (client) => {
    setDeleteModal({ open: true, client });
  };

  const confirmDelete = async () => {
    const clientToDelete = deleteModal.client;
    setDeleteModal({ open: false, client: null });
    
    if (!clientToDelete?.id) {
      showAlert('Invalid client data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await organizationApi.deleteClient(clientToDelete.id);
      await fetchClients();
      showAlert('Client deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting client:', err);
      showAlert(err.message || 'Failed to delete client.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes((searchTerm || '').toLowerCase());
    const matchesCountries = selectedCountries.length === 0 || selectedCountries.includes(client.country.id);
    return matchesSearch && matchesCountries;
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/organization')}>
            <FaArrowLeft /> Back to Organization
          </BackButton>
          <Title>Clients Configuration</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search Client"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                minWidth: '300px',
                flex: 1,
              }}
            />

            <FormControl sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel id="countries-filter-label">Filter by Countries</InputLabel>
              <Select
                labelId="countries-filter-label"
                multiple
                value={selectedCountries}
                onChange={(e) => setSelectedCountries(e.target.value)}
                input={<OutlinedInput label="Filter by Countries" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip 
                        key={value} 
                        label={countries.find(c => c.id === value)?.name} 
                        size="small"
                      />
                    ))}
                  </Box>
                )}
                size="small"
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <HeaderButton 
              onClick={() => setClientModal({
                open: true,
                isEdit: false,
                initialData: null
              })} 
              style={{ whiteSpace: 'nowrap' }}
            >
              <FaPlus /> Add Client
            </HeaderButton>
          </ControlsContainer>

          <MainContainer $show={showControls}>
            <div style={{ 
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px'
            }}>
              <Grid 
                container 
                spacing={3}
                sx={{
                  width: '100%',
                  margin: '0 auto',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(4, 1fr)'
                  },
                  gap: '24px',
                  '& .MuiGrid-item': {
                    padding: '0',
                    width: '100%'
                  }
                }}
              >
                {filteredClients.map((client) => (
                  <Grid item key={client.id}>
                    <Card 
                      sx={{ 
                        width: '100%',
                        minHeight: '140px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          backgroundColor: '#f1f3f5',
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '16px !important',
                        flexGrow: 1,
                        position: 'relative'
                      }}>
                        {client.partyType && (
                          <Chip
                            label={getClientPartyTypeName(client.partyType)}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              height: '20px',
                              fontSize: '11px',
                              fontWeight: 500,
                              backgroundColor: getClientPartyTypeBackgroundColor(client.partyType),
                              color: getClientPartyTypeTextColor(client.partyType),
                            }}
                          />
                        )}
                        <Box>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 500,
                              color: '#212529',
                              marginBottom: '8px',
                              paddingRight: client.partyType ? '60px' : '0'
                            }}
                          >
                            {client.name}
                          </Typography>

                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#6c757d',
                              fontSize: '12px',
                              marginBottom: '4px'
                            }}
                          >
                            Country: {client.country.name}
                          </Typography>

                        </Box>
                        
                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '8px',
                          marginTop: '16px'
                        }}>
                          <Button
                            onClick={() => setClientModal({
                              open: true,
                              isEdit: true,
                              initialData: {
                                id: client.id,
                                name: client.name,
                                countryId: client.country?.id || '',
                                partyType: client.partyType || ''
                              }
                            })}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#0d6efd',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(13, 110, 253, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(13, 110, 253, 0.15)',
                                color: '#0d6efd'
                              }
                            }}
                          >
                            Edit
                          </Button>

                          <Button
                            onClick={() => handleDeleteClient(client)}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#dc3545',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(220, 53, 69, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                color: '#dc3545'
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </MainContainer>
        </div>
      </Content>

      {/* Unified modal for both adding and editing clients */}
      <ClientModal
        isOpen={clientModal.open}
        onClose={handleCloseClientModal}
        onClientCreated={handleClientCreated}
        initialData={clientModal.initialData}
        isEdit={clientModal.isEdit}
      />

      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, client: null })}
        onConfirm={confirmDelete}
        title="Delete Client"
        message={`Are you sure you want to delete ${deleteModal.client?.name}?`}
        actionButtonText="Delete"
      />
    </Container>
  );
};

export default ClientsConfiguration; 